import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Stack,
  Grid,
  Divider,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  VerifiedRounded,
  AutoAwesomeRounded,
  ElectricCarRounded,
  TimelineRounded,
  SupportAgentRounded,
  ArrowForwardRounded,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPartnerDisplayName, getPartnerTheme, getPartnerWidgets, isPartnerMaintenance, isPartnerComingSoon } from '../common/partnerUtils';
import PartnerMeta from './PartnerMeta.jsx';

const badgeIcons = [VerifiedRounded, AutoAwesomeRounded, ElectricCarRounded];
const statIcons = [VerifiedRounded, AutoAwesomeRounded, ElectricCarRounded, SupportAgentRounded];

const WidgetCard = ({ children, sx, contentSx }) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: 'var(--partner-surface)',
      borderRadius: 3,
      border: '1px solid rgba(148,163,184,0.25)',
      boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 26px 60px rgba(15,23,42,0.16)',
      },
      ...sx,
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3 }, ...contentSx }}>{children}</CardContent>
  </Card>
);

export default function PartnerLanding({ partner, bookingPath, disableMeta }) {
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const navigate = useNavigate();
  const partnerName = getPartnerDisplayName(partner, settings) || 'Partner';
  const theme = getPartnerTheme(partner);
  const widgets = getPartnerWidgets(partner, settings);
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const isMaintenance = isPartnerMaintenance(partner);
  const isComingSoon = isPartnerComingSoon(partner);
  const brandLogo = partner?.logoUrl || partner?.logo || partner?.logoURL || '';

  const handleCta = (path = bookingPath || '/addbookings') => {
    if (auth?.profile?.uid) {
      navigate(path, { state: { partner } });
    } else {
      navigate('/login', { state: { from: path } });
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.background,
        color: theme.text,
        fontFamily: theme.fontBody,
        position: 'relative',
        overflow: 'hidden',
        '@keyframes partnerFadeUp': {
          from: { opacity: 0, transform: 'translateY(18px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
      style={{
        '--partner-primary': theme.primary,
        '--partner-secondary': theme.secondary,
        '--partner-accent': theme.accent,
        '--partner-surface': theme.surface,
        '--partner-text': theme.text,
        '--partner-muted': theme.muted,
      }}
    >
      {disableMeta ? null : <PartnerMeta partner={partner} />}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.6,
          background:
            'radial-gradient(circle at 15% 10%, rgba(125,211,252,0.25), transparent 50%), radial-gradient(circle at 85% 5%, rgba(251,191,36,0.18), transparent 40%), radial-gradient(circle at 60% 80%, rgba(148,163,184,0.18), transparent 45%)',
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {isAdmin && (isMaintenance || isComingSoon) ? (
          <Box
            sx={{
              bgcolor: '#fef3c7',
              color: '#92400e',
              borderBottom: '1px solid #fde68a',
              py: 1.5,
              px: { xs: 3, md: 6 },
              fontWeight: 600,
            }}
          >
            {isComingSoon
              ? 'Coming soon is ON. Customers see the coming soon page.'
              : 'Maintenance mode is ON. Customers see the maintenance page.'}
          </Box>
        ) : null}
        {widgets.filter((widget) => widget.enabled !== false).map((widget, index) => {
        switch (widget.type) {
          case 'hero': {
            const data = widget.data || {};
            return (
              <Box
                key={`widget-${index}`}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${theme.heroFrom} 0%, ${theme.heroTo} 60%)`,
                  color: '#fff',
                  py: { xs: 8, md: 12 },
                  animation: 'partnerFadeUp 0.7s ease both',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.15,
                    background:
                      'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5), transparent 55%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.35), transparent 50%)',
                  }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative' }}>
                  <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        {brandLogo ? (
                          <Box
                            component="img"
                            src={brandLogo}
                            alt={partnerName}
                            sx={{ height: 36, width: 'auto', objectFit: 'contain' }}
                          />
                        ) : null}
                        <Chip
                          label={data.eyebrow || 'Partner experience'}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            fontWeight: 600,
                            alignSelf: 'flex-start',
                          }}
                        />
                        <Typography
                          variant="h2"
                          sx={{
                            fontFamily: theme.fontHeading,
                            fontWeight: 700,
                            fontSize: { xs: 36, md: 52 },
                            lineHeight: 1.05,
                          }}
                        >
                          {data.title}
                        </Typography>
                        <Typography sx={{ fontSize: 18, color: 'rgba(255,255,255,0.75)', maxWidth: 520 }}>
                          {data.subtitle}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Button
                            variant="contained"
                            onClick={() => handleCta(bookingPath || '/addbookings')}
                            sx={{
                              bgcolor: '#fff',
                              color: theme.primary,
                              textTransform: 'none',
                              fontWeight: 700,
                              px: 4,
                              py: 1.5,
                              borderRadius: 999,
                              '&:hover': { bgcolor: '#f8fafc' },
                            }}
                          >
                            {data.ctaPrimary || 'Book now'}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => navigate('/contact-us')}
                            sx={{
                              borderColor: 'rgba(255,255,255,0.5)',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 4,
                              py: 1.5,
                              borderRadius: 999,
                              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                            }}
                          >
                            {data.ctaSecondary || 'Talk to sales'}
                          </Button>
                        </Stack>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap">
                          {(data.badges || []).map((badge, idx) => {
                            const Icon = badgeIcons[idx % badgeIcons.length];
                            return (
                              <Chip
                                key={badge}
                                icon={<Icon sx={{ color: '#fff' }} />}
                                label={badge}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.15)',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <WidgetCard
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(14px)',
                        }}
                      >
                        <Box sx={{ display: 'grid', gap: 2 }}>
                          <Typography sx={{ fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', fontSize: 12 }}>
                            {partnerName} Dispatch Stack
                          </Typography>
                          <Box sx={{ display: 'grid', gap: 1.5 }}>
                            {['Premium pickup flow', 'Live fleet monitoring', 'Corporate SLA ready'].map((item) => (
                              <Box
                                key={item}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  bgcolor: 'rgba(255,255,255,0.12)',
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1.5,
                                }}
                              >
                                <VerifiedRounded sx={{ color: '#fff' }} />
                                <Typography sx={{ fontWeight: 600 }}>{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                          {data.image ? (
                            <Box
                              component="img"
                              src={data.image}
                              alt={partnerName}
                              sx={{
                                width: '100%',
                                borderRadius: 2,
                                objectFit: 'cover',
                                maxHeight: 220,
                                filter: 'drop-shadow(0 20px 35px rgba(0,0,0,0.25))',
                              }}
                            />
                          ) : null}
                        </Box>
                      </WidgetCard>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'stats': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 8 }, animation: 'partnerFadeUp 0.7s ease 0.1s both' }}>
                <Container maxWidth="lg">
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 28 }}>
                        {data.title}
                      </Typography>
                      <Typography sx={{ color: theme.muted, mt: 1 }}>{data.subtitle}</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2}>
                        {(data.items || []).map((item, idx) => {
                          const Icon = statIcons[idx % statIcons.length];
                          return (
                          <Grid item xs={6} md={3} key={item.label}>
                            <WidgetCard sx={{ textAlign: 'center' }}>
                              <Avatar
                                sx={{
                                  bgcolor: 'rgba(15,23,42,0.08)',
                                  color: theme.primary,
                                  width: 42,
                                  height: 42,
                                  mx: 'auto',
                                  mb: 1.5,
                                }}
                              >
                                <Icon fontSize="small" />
                              </Avatar>
                              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{item.value}</Typography>
                              <Typography sx={{ color: theme.muted, fontSize: 13 }}>{item.label}</Typography>
                            </WidgetCard>
                          </Grid>
                        );
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'fleet': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, animation: 'partnerFadeUp 0.7s ease 0.15s both' }}>
                <Container maxWidth="lg">
                  <Stack spacing={1} sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30 }}>
                      {data.title}
                    </Typography>
                    <Typography sx={{ color: theme.muted }}>{data.subtitle}</Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {(data.items || []).map((item) => (
                      <Grid item xs={12} md={4} key={item.name}>
                        <WidgetCard sx={{ height: '100%', display: 'grid', gap: 1.5 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{item.name}</Typography>
                          <Typography sx={{ color: theme.muted, fontSize: 14 }}>Seats: {item.seats}</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {(item.features || []).map((feature) => (
                              <Chip key={feature} label={feature} size="small" sx={{ bgcolor: 'rgba(15,23,42,0.06)' }} />
                            ))}
                          </Stack>
                        </WidgetCard>
                      </Grid>
                    ))}
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'coverage': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, bgcolor: 'rgba(15,23,42,0.04)', animation: 'partnerFadeUp 0.7s ease 0.2s both' }}>
                <Container maxWidth="lg">
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30 }}>
                        {data.title}
                      </Typography>
                      <Typography sx={{ color: theme.muted, mt: 1 }}>{data.subtitle}</Typography>
                      <Divider sx={{ my: 3 }} />
                      <Stack spacing={1}>
                        {(data.areas || []).map((area) => (
                          <Typography key={area} sx={{ fontWeight: 600 }}>
                            - {area}
                          </Typography>
                        ))}
                      </Stack>
                      {data.note ? <Typography sx={{ mt: 2, color: theme.muted }}>{data.note}</Typography> : null}
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <WidgetCard sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
                        <TimelineRounded sx={{ fontSize: 60, color: theme.primary, opacity: 0.6 }} />
                        <Typography sx={{ fontWeight: 600 }}>Dynamic coverage map preview</Typography>
                        <Typography sx={{ color: theme.muted, fontSize: 13 }}>
                          Zones and airport queues update in real time.
                        </Typography>
                      </WidgetCard>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'pricing': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, animation: 'partnerFadeUp 0.7s ease 0.25s both' }}>
                <Container maxWidth="lg">
                  <Stack spacing={1} sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30 }}>
                      {data.title}
                    </Typography>
                    <Typography sx={{ color: theme.muted }}>{data.subtitle}</Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {(data.plans || []).map((plan) => (
                      <Grid item xs={12} md={4} key={plan.name}>
                        <WidgetCard
                          sx={{
                            border: plan.highlight ? `2px solid ${theme.accent}` : undefined,
                            transform: plan.highlight ? 'translateY(-6px)' : 'none',
                            boxShadow: plan.highlight ? '0 28px 60px rgba(15,23,42,0.2)' : undefined,
                          }}
                        >
                          <Stack spacing={2}>
                            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{plan.name}</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: 26 }}>{plan.price}</Typography>
                            <Stack spacing={1}>
                              {(plan.features || []).map((feature) => (
                                <Typography key={feature} sx={{ color: theme.muted, fontSize: 14 }}>
                                  - {feature}
                                </Typography>
                              ))}
                            </Stack>
                            <Button
                              variant={plan.highlight ? 'contained' : 'outlined'}
                              endIcon={<ArrowForwardRounded />}
                              onClick={() => navigate('/contact-us')}
                              sx={{
                                mt: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                ...(plan.highlight
                                  ? { bgcolor: theme.accent, color: '#0b0b0b' }
                                  : { borderColor: 'rgba(15,23,42,0.2)', color: theme.text }),
                              }}
                            >
                              {plan.highlight ? 'Scale now' : 'Learn more'}
                            </Button>
                          </Stack>
                        </WidgetCard>
                      </Grid>
                    ))}
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'testimonials': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, bgcolor: '#0b0b0b', color: '#fff', animation: 'partnerFadeUp 0.7s ease 0.3s both' }}>
                <Container maxWidth="lg">
                  <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30, mb: 3 }}>
                    {data.title}
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    {(data.items || []).map((item) => (
                      <WidgetCard
                        key={item.name}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.08)',
                          borderColor: 'rgba(255,255,255,0.15)',
                          color: '#fff',
                        }}
                      >
                        <Typography sx={{ fontSize: 16, mb: 2 }}>"{item.quote}"</Typography>
                        <Typography sx={{ fontWeight: 700 }}>{item.name}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>{item.role}</Typography>
                      </WidgetCard>
                    ))}
                  </Stack>
                </Container>
              </Box>
            );
          }
          case 'safety': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, animation: 'partnerFadeUp 0.7s ease 0.35s both' }}>
                <Container maxWidth="lg">
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30 }}>
                        {data.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2}>
                        {(data.items || []).map((item) => (
                          <Grid item xs={12} md={4} key={item.title}>
                            <WidgetCard sx={{ height: '100%' }}>
                              <SupportAgentRounded sx={{ color: theme.primary, mb: 1 }} />
                              <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
                              <Typography sx={{ color: theme.muted, fontSize: 14 }}>{item.desc}</Typography>
                            </WidgetCard>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            );
          }
          case 'cta': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, bgcolor: 'rgba(15,23,42,0.06)', animation: 'partnerFadeUp 0.7s ease 0.4s both' }}>
                <Container maxWidth="md">
                  <WidgetCard sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 26 }}>
                        {data.title}
                      </Typography>
                      <Typography sx={{ color: theme.muted, mt: 1 }}>{data.subtitle}</Typography>
                    </Box>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardRounded />}
                      onClick={() => handleCta(bookingPath || '/addbookings')}
                      sx={{
                        bgcolor: theme.primary,
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 999,
                        '&:hover': { bgcolor: '#0f172a' },
                      }}
                    >
                      {data.ctaLabel || 'Activate partnership'}
                    </Button>
                  </WidgetCard>
                </Container>
              </Box>
            );
          }
          case 'apps': {
            const data = widget.data || {};
            return (
              <Box key={`widget-${index}`} sx={{ py: { xs: 6, md: 9 }, animation: 'partnerFadeUp 0.7s ease 0.45s both' }}>
                <Container maxWidth="lg">
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontFamily: theme.fontHeading, fontWeight: 700, fontSize: 30 }}>
                        {data.title}
                      </Typography>
                      <Typography sx={{ color: theme.muted, mt: 1 }}>{data.subtitle}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {(data.badges || []).map((badge) => (
                          <Button
                            key={badge}
                            variant="outlined"
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderColor: 'rgba(15,23,42,0.2)',
                              color: theme.text,
                              px: 3,
                              py: 1.5,
                              borderRadius: 999,
                            }}
                          >
                            {badge}
                          </Button>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            );
          }
          default:
            return null;
        }
      })}
      </Box>
    </Box>
  );
}
